// src/components/transactions/TxDetailDrawer.tsx
import React, { useState } from "react";
import { Button, Descriptions, Drawer, Empty, Flex, Tag, App, Tooltip } from "antd";
import { FileSearchOutlined } from "@ant-design/icons";
import type { ITransactions } from "src/types/types";
import { currencyFmt, safeDate } from "./helpers";

type Props = { open: boolean; onClose: () => void; tx: ITransactions | null };

/** Backend path'ini üret (api baseURL tarafı .env / api instance üzerinde) */
function resolveEDocPath(tx: ITransactions): string | null {
  // Örn: GET /transactions/document/:documentNumber  (PDF döner)
  return tx.DocumentNumber ? `/transactions/document/${encodeURIComponent(tx.DocumentNumber)}` : null;
}

/** Content-Disposition içinden dosya adını çek */
function parseFileNameFromDisposition(disposition: string | null, fallback: string) {
  if (!disposition) return fallback;
  const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(disposition);
  return decodeURIComponent(m?.[1] || m?.[2] || fallback);
}

/** Yetkili PDF isteği (Bearer token) → blob → yeni sekmede aç (popup bloklanırsa indir) */
async function openProtectedPdfWithFetch(fullUrl: string, fallbackName: string) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (!token) throw new Error("Oturum bulunamadı (token yok).");

  const res = await fetch(fullUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    },
    // Cookie tabanlı auth kullanıyorsan aç:
    // credentials: "include",
  });

  if (!res.ok) {
    // Hata gövdesini okumayı dene (kısa bir önizleme)
    let detail = "";
    try { detail = (await res.text()).slice(0, 200); } catch {}
    throw new Error(`PDF alınamadı (HTTP ${res.status}) ${detail}`);
  }

  const fileName = parseFileNameFromDisposition(res.headers.get("Content-Disposition"), fallbackName);
  const blob = await res.blob();

  const objectUrl = URL.createObjectURL(blob);
  const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!win || win.closed) {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fileName;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export default function TxDetailDrawer({ open, onClose, tx }: Props) {
  const { message } = App.useApp();
  const [opening, setOpening] = useState(false);

  const handleOpenEDoc = async () => {
    if (!tx) return;

    const path = resolveEDocPath(tx);
    if (!path) {
      message.warning("Bu kayıt için e-Belge bilgisi bulunamadı.");
      return;
    }

    try {
      setOpening(true);
      // api instance'ın baseURL'ini kullanabilmek için absolute URL üret
      // window.__API_BASE__ gibi bir şey yoksa, .env (VITE…) ile de verebilirsin.
      const baseURL =
        (import.meta as any)?.env?.VITE_API_URL ||
        "http://localhost:3009/api/v1"; // fallback
      const fullUrl = path.startsWith("http") ? path : `${baseURL.replace(/\/$/, "")}${path}`;

      await openProtectedPdfWithFetch(fullUrl, `${tx.DocumentNumber}.pdf`);
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || "e-Belge açılamadı");
    } finally {
      setOpening(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title={
        <Flex align="center" justify="space-between">
          <span>Fatura Detayı</span>
          {tx?.DocumentNumber ? <Tag>{tx.DocumentNumber}</Tag> : null}
        </Flex>
      }
    >
      {tx ? (
        <>
          <Descriptions bordered column={1} size="middle" labelStyle={{ width: 180 }}>
            <Descriptions.Item label="Belge No">{tx.DocumentNumber || "-"}</Descriptions.Item>
            <Descriptions.Item label="Tarih">{safeDate(tx.DocumentDate)}</Descriptions.Item>
            <Descriptions.Item label="Müşteri">{tx.RelationName || "-"}</Descriptions.Item>
            <Descriptions.Item label="İşlem No">{tx.TransactionNumber || "-"}</Descriptions.Item>
            <Descriptions.Item label="Para Birimi">{tx.Currency || "-"}</Descriptions.Item>
            <Descriptions.Item label="Toplam">{currencyFmt(tx.TotalSell, tx.Currency)}</Descriptions.Item>
            <Descriptions.Item label="Ödenen">{currencyFmt(tx.PaidAmount, tx.Currency)}</Descriptions.Item>
            <Descriptions.Item label="Kalan">{currencyFmt(tx.UnpaidAmount, tx.Currency)}</Descriptions.Item>
            <Descriptions.Item label="e-Belge No">{tx.EDocumentNumber || "-"}</Descriptions.Item>
            <Descriptions.Item label="UUID">{tx.UUID || "-"}</Descriptions.Item>
            <Descriptions.Item label="Not/Referans">{tx.Reference || "-"}</Descriptions.Item>
            <Descriptions.Item label="Oluşturulma">{safeDate(tx.CreatedAt)}</Descriptions.Item>
          </Descriptions>

          <Flex justify="end" gap={8} style={{ marginTop: 16 }} wrap>
            <Tooltip title="e-Belgeyi yeni sekmede aç">
              <Button
                icon={<FileSearchOutlined />}
                onClick={handleOpenEDoc}
                loading={opening}
                disabled={!tx.DocumentNumber}
              >
                e-Belge
              </Button>
            </Tooltip>
          </Flex>
        </>
      ) : (
        <Empty description="Fatura seçilmedi" />
      )}
    </Drawer>
  );
}
